<script setup lang="ts">
import type { IconName } from "../Icon/icons";
import { computed, useTemplateRef } from "vue";
import ACTooltip from "../ACTooltip/ACTooltip.vue";
import VIcon from "../Icon/VIcon.vue";
import { useComponentId } from "../useComponentId";

const {
  summary,
  iconLeft = undefined,
  invalid = false,
  disabled = false,
  hasPerms = true,
  ariaLabel = undefined,
  size = "small",
} = defineProps<{
  summary: string
  iconLeft?: IconName
  invalid?: boolean
  disabled?: boolean
  hasPerms?: boolean
  ariaLabel?: string
  size?: "small" | "medium"
}>();
const emit = defineEmits<{ (e: "toggle", open: boolean): void }>();
const open = defineModel<boolean>({ default: false });
const id = useComponentId();
const contentId = computed(() => `field-disclosure-${id}`);
const toggleRef = useTemplateRef<HTMLButtonElement>("toggleRef");
const isDisabled = computed(() => disabled || !hasPerms);

function setOpen(next: boolean) {
  open.value = next;
  emit("toggle", next);
}

function onKeydown(event: KeyboardEvent) {
  if (event.key !== "Escape" || !open.value) {
    return;
  }
  event.preventDefault();
  event.stopPropagation();
  setOpen(false);
  toggleRef.value?.focus();
}
</script>

<template>
  <div @keydown="onKeydown">
    <div class="flex items-center gap-2">
      <ACTooltip type="edit_field" :allow-change="hasPerms">
        <button
          ref="toggleRef"
          type="button"
          class="relative flex min-w-0 flex-1 items-center gap-2 rounded-md text-left before:absolute before:inset-x-0 before:-inset-y-1 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500"
          :class="[
            size === 'small' ? 'h-8 text-sm' : 'h-10 text-base',
            invalid ? 'text-red-500' : 'text-gray-700 dark:text-gray-200',
            isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
          ]"
          :disabled="isDisabled"
          :aria-expanded="open"
          :aria-controls="contentId"
          :aria-label="ariaLabel"
          @click="!isDisabled && setOpen(!open)"
        >
          <VIcon v-if="iconLeft" :name="iconLeft" class="shrink-0 text-gray-500 dark:text-gray-400" aria-hidden="true" />
          <span class="min-w-0 truncate tabular-nums">
            <slot name="summary" :open="open">{{ summary }}</slot>
          </span>
          <VIcon name="chevron-down" class="shrink-0 text-gray-400 transition-transform duration-200" :class="{ 'rotate-180': open }" aria-hidden="true" />
        </button>
      </ACTooltip>
      <div class="shrink-0">
        <slot name="trailing" />
      </div>
    </div>
    <Transition
      enter-active-class="[transition-property:opacity,transform] duration-200 ease-[cubic-bezier(0.2,0,0,1)]"
      enter-from-class="translate-y-1 opacity-0"
      enter-to-class="translate-y-0 opacity-100"
      leave-active-class="[transition-property:opacity,transform] duration-150"
      leave-from-class="translate-y-0 opacity-100"
      leave-to-class="translate-y-1 opacity-0"
    >
      <div v-show="open" :id="contentId" role="group" class="pt-2">
        <slot />
      </div>
    </Transition>
  </div>
</template>
